import { all, call, fork, put, takeEvery, takeLatest, throttle, select } from 'redux-saga/effects';
import Axios from 'axios';
import i18n from 'i18next';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { createCookies } from 'helper/cookies';
import {
  userSignOutSuccess,
  AuthPayload,
  userSignInSuccess,
  userSignInError,
  validateRegistrationTokenError,
  validateRegistrationTokenSuccess,
  acceptInvitationSuccess,
  acceptInvitationError,
  updateLastActiveSuccess,
  requestResetPasswordError,
  requestResetPasswordSuccess,
  resetPasswordError,
  resetPasswordSuccess,
  validatePasswordResetTokenSuccess,
  validatePasswordResetTokenError,
  updateAuthUserDataSuccess,
  updateAuthUserDataError,
  userSignIn,
} from 'actions/Auth';

import {
  SIGNIN_USER,
  SIGNOUT_USER,
  VALIDATE_REGISTRATION_TOKEN,
  ACCEPT_INVITATION,
  UPDATE_LAST_ACTIVE,
  REQUEST_RESET_PASSWORD,
  RESET_PASSWORD,
  VALIDATE_PASSWORD_RESET_TOKEN,
  UPDATE_AUTH_USER_DATA,
} from '../constants/ActionTypes';

import {
  signInUserRequest,
  signOutUserRequest,
  getRegistrationTokenData,
  acceptInvitation,
  requestResetPassword,
  getResetPasswordToken,
  resetPassword,
  impersonateRequest,
} from '../api/Auth';

import {
  getUserById
} from '../api/User';

import {
  showError, clearMessage, showMessage
} from 'actions/Common';
import { CustomAction } from '../model/state';
import { User, MessageType } from '../model';
import { toUser, toInvitationData, fromUserToAcceptInvitation } from 'model/api';
import {selectLoggedUser, selectUserId} from "../selectors";
import { analytics } from 'utils/Analytics';

const browserCache = createCookies();

const signIn = async (username: string, password: string): Promise<any> => {
  const response = await signInUserRequest(username, password);
  return {
    token: `Bearer ${response.access_token}`,
    userId: response.user_id
  };
}

function* signInUser(action: CustomAction<AuthPayload>) {
  const { username, password, impersonateAs } = (action.payload && action.payload.credentials) || { username: '', password: '' };
  try {

    const response = (impersonateAs && !isEmpty(impersonateAs)) ? 
      yield call(impersonateRequest, { 
        email: impersonateAs, 
        impersonatorEmail: username, 
        impersonatorPassword: password
      }) : 
      yield call(signIn, username, password);

    browserCache.set('token', response.token);
    browserCache.set('userId', response.userId);
    Axios.defaults.headers.common['Authorization'] = response.token;

    const userResponse = yield call(getUserById, response.userId, ['locationRoles']);
    const user: User = toUser(userResponse);

    if (user?.id) analytics.trackLogin(user.id); // Google Analytics tracking for successful login

    yield put(userSignInSuccess(user, response.token));
    yield put(clearMessage());
  } catch (err) {
    yield put(userSignInError());
    if (err.response && (err.response.status === 400 || err.response.status === 401)) {
      yield put(showError(i18n.t(err.response.data.message)));
    } else {
      yield put(showError(i18n.t('connection_error'), err));
    }
  }

}

function* signOutUser(action: CustomAction<AuthPayload>) {
  const history = action.payload?.history;
  if (history) {
    history.push('/');
  }

  if (browserCache.get('token') || Axios.defaults.headers.common['Authorization']) {
    window.loggingOut = true; //flag to signal the intention, in case request fails
    try {
      yield call(signOutUserRequest);
      delete window.loggingOut;
    } catch (error) {
      // silently ignore error, most probably failed due to an expired token
    }
  }
  browserCache.remove('token');
  browserCache.remove('userId');
  browserCache.remove('user');
  browserCache.remove('lastActive');
  yield put(updateLastActiveSuccess(undefined));

  delete Axios.defaults.headers.common['Authorization'];
  yield put(userSignOutSuccess());
  yield put(clearMessage());
}

function* validateRegistrationTokenWorker(action: CustomAction<AuthPayload>) {
  const token = action.payload?.token;
  try {
    if (!token) {
      throw Error('Invalid Parameters: Token field is missing');
    }
    window.isValidatingRegistrationToken = true; // to avoid redirection to login if the token is not valid.
    const response = yield call(getRegistrationTokenData, token);

    yield put(validateRegistrationTokenSuccess(toInvitationData(response)));
    yield put(clearMessage());
  } catch (err) {
    yield put(validateRegistrationTokenError());
    if (err.response && err.response.status === 401) {
      yield put(showMessage({
        type: MessageType.ERROR,
        message: i18n.t('user_registration_invalid_token_message'),
        description: i18n.t('user_registration_invalid_token_description'),
      }));
    } else {
      yield put(showError(i18n.t('connection_error')));
    }
  }
}

function* acceptInvitationWorker(action: CustomAction<AuthPayload>) {
  const user = action.payload?.user;
  const creds = action.payload?.credentials;
  const token = action.payload?.token;
  try {
    if (!user || !creds || !token) {
      throw Error('Invalid Parameters: User data is missing');
    }
    yield put(clearMessage());
    yield call(acceptInvitation, fromUserToAcceptInvitation(user, creds), token);
    yield put(userSignIn(creds));

    yield put(acceptInvitationSuccess());
    yield put(showMessage({
      type: MessageType.SUCCESS,
      message: i18n.t('user_registration_accept_success_message'),
      description: i18n.t('user_registration_accept_success_description'),
    }));
  } catch (err) {
    yield put(acceptInvitationError());
    if (err.response && err.response.status === 400) {
      yield put(showMessage({
        type: MessageType.ERROR,
        message: i18n.t('user_registration_accept_400_message'),
        description: i18n.t('user_registration_accept_400_description'),
      }));
    } else {
      yield put(showMessage({
        type: MessageType.ERROR,
        message: i18n.t('user_registration_accept_error_message'),
        description: i18n.t('user_registration_accept_error_description'),
      }));
    }
  }
}

function* updateLastActiveWorker(action: CustomAction<AuthPayload>) {
  try {
    const now = moment();
    browserCache.set('lastActive', now.toISOString());
    yield put(updateLastActiveSuccess(now.toISOString()));
  } catch (err) {
    console.error(err);
  }
}

function* requestResetPasswordWorker(action: CustomAction<AuthPayload>) {
  const email = action.payload?.email;
  const locale = action.payload?.locale;
  try {
    if (!email || !locale) {
      throw Error('Invalid Parameters');
    }
    yield call(requestResetPassword, { email, app: 'mud', locale: locale.toLowerCase() });
    yield put(requestResetPasswordSuccess());
    yield put(showMessage({ message: i18n.t('request-reset-password-title'), description: i18n.t('request-reset-password-description'), type: MessageType.SUCCESS }));
  } catch (err) {
    if (err.response?.status === 429) {
      yield put(showError(i18n.t('request-reset-password-error-limit-reached')));
    } else {
      yield put(showError(i18n.t('connection_error'), err));
      yield put(requestResetPasswordError());
    }
  }
}

function* resetPasswordWorker(action: CustomAction<AuthPayload>) {
  const credentials = action.payload?.credentials;
  const token = action.payload?.token;
  try {
    if (!token || !credentials) {
      throw Error('Invalid Parameters');
    }
    yield call(resetPassword, credentials.password, token);
    yield put(resetPasswordSuccess());
    yield put(showMessage({ message: i18n.t('reset-password-title'), description: i18n.t('reset-password-description'), type: MessageType.SUCCESS }));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(resetPasswordError());
  }
}

function* validateResetPasswordTokenWorker(action: CustomAction<AuthPayload>) {
  const token = action.payload?.token;
  const userId = action.payload?.userId;
  try {
    if (!token || !userId) {
      throw Error('Invalid Parameters');
    }
    window.isValidatingRegistrationToken = true; // to avoid redirection to login if the token is not valid.

    const result = yield call(getResetPasswordToken, userId, token);
    yield put(validatePasswordResetTokenSuccess(result.token));
    yield put(clearMessage());
  } catch (err) {
    yield put(validatePasswordResetTokenError());

    if (err.response?.status === 401 || err.response?.status === 404) {
      yield put(showMessage({
        type: MessageType.ERROR,
        message: i18n.t('reset_password_invalid_token_message'),
        description: i18n.t('reset_password_invalid_token_description'),
      }));
    } else {
      yield put(showError(i18n.t('connection_error')));
    }
  }
}

function* updateAuthUserDataWorker() {
  const userId = yield select(selectUserId());
  try {
    if (!userId) {
      throw Error('Invalid Parameters: User id not found in the store');
    }
    const current: User | undefined = yield select(selectLoggedUser());
    if (!current || current.id !== userId) {
      const userResponse = yield call(getUserById, userId, ['locationRoles']);
      yield put(updateAuthUserDataSuccess(toUser(userResponse)));
    }
  } catch (err) {
    yield put(updateAuthUserDataError());
    yield put(showError(i18n.t('connection_error'), err));
  }
}

export function* signInUserSaga() {
  yield takeEvery(SIGNIN_USER, signInUser);
}

export function* signOutUserSaga() {
  yield takeLatest(SIGNOUT_USER, signOutUser);
}

export function* validateRegistrationTokenSaga() {
  yield takeLatest(VALIDATE_REGISTRATION_TOKEN, validateRegistrationTokenWorker)
}

export function* acceptInvitationSaga() {
  yield takeLatest(ACCEPT_INVITATION, acceptInvitationWorker)
}

export function* updateLastActiveSaga() {
  yield throttle(10000, UPDATE_LAST_ACTIVE, updateLastActiveWorker)
}

export function* requestResetPasswordSaga() {
  yield takeLatest(REQUEST_RESET_PASSWORD, requestResetPasswordWorker);
}

export function* resetPasswordSaga() {
  yield takeLatest(RESET_PASSWORD, resetPasswordWorker);
}

export function* validateResetPasswordTokenSaga() {
  yield takeLatest(VALIDATE_PASSWORD_RESET_TOKEN, validateResetPasswordTokenWorker);
}

export function* updateAuthUserDataSaga() {
  yield takeLatest(UPDATE_AUTH_USER_DATA, updateAuthUserDataWorker)
}

export default function* rootSaga() {
  yield all([
    fork(signInUserSaga),
    fork(signOutUserSaga),
    fork(validateRegistrationTokenSaga),
    fork(acceptInvitationSaga),
    fork(updateLastActiveSaga),
    fork(requestResetPasswordSaga),
    fork(resetPasswordSaga),
    fork(validateResetPasswordTokenSaga),
    fork(updateAuthUserDataSaga),
  ]);
}
